<template>
  <v-list-group v-model="expandStatus" :value="item.group" :subgroup="subGroup">
    <template v-slot:activator="{ props }">
      <v-list-item
        v-bind="props"
        class="base-item-group"
        :class="expandStatus ? activeGroupClass : ''"
        @click.capture="onGroupClick">
        <template v-if="text" v-slot:prepend>
          <div class="v-list-item__icon--text">{{ computedText }}</div>
        </template>

        <template v-else-if="item.icon" v-slot:prepend>
          <v-icon v-text="item.icon" />
        </template>

        <template #title>
          <span class="text-14 pl-0 localFont">{{ item.title }}</span>
        </template>
      </v-list-item>
    </template>

    <template v-for="(child, i) in children">
      <base-item-sub-group
        v-if="child.children"
        :key="`sub-group-${i}`"
        :item="child"
        class="" />

      <base-item
        v-else
        :key="`item-${i}`"
        class=""
        :item="child"
        :text="false" />
    </template>
  </v-list-group>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import kebabCase from 'lodash/kebabCase'
import themeMixin from '@/mixin/themeMixin'
export default defineComponent({
  name: 'ItemGroup',
  mixins: [themeMixin],

  inheritAttrs: false,
  props: {
    item: {
      type: Object,
      default: () => ({
        avatar: undefined,
        group: undefined,
        title: undefined,
        children: [],
      }),
    },
    subGroup: {
      type: Boolean,
      default: false,
    },
    text: {
      type: Boolean,
      default: false,
    },
    expand: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /** @type {boolean} */
      internalExpand: this.expand,
    }
  },

  computed: {
    expandStatus: {
      get() {
        return this.internalExpand
      },
      set(val) {
        this.internalExpand = val
      },
    },
    getThemeMode() {
      return this.themeMode
    },
    isDark() {
      return Boolean(this.themeMode?.isDark)
    },
    activeGroupClass() {
      if (this.isDark && this.getThemeMode.verticalSidebarDrawerColor !== 'white') {
        return `${this.getThemeMode.verticalSidebarDrawerColor} lighten-0 white--text`
      }
      return 'grey-lighten-'
    },
    children() {
      return this.item.children.map((item) => {
        if (!item.to) {
          return { ...item, to: undefined }
        }
        return {
          ...item,
          to: this.resolveChildPath(item.to),
        }
      })
    },
    computedText() {
      if (!this.item || !this.item.title) return ''

      let text = ''

      this.item.title.split(' ').forEach((val) => {
        text += val.substring(0, 1)
      })

      return text
    },
    group() {
      return this.genGroup(this.item.children)
    },
  },

  methods: {
    resolveChildPath(childTo) {
      if (!childTo) return childTo

      const parentTo = this.item.to || ''
      if (!childTo.startsWith('/')) {
        const parent = parentTo.endsWith('/') ? parentTo.slice(0, -1) : parentTo
        return `${parent}/${childTo}`
      }

      if (!parentTo) return childTo

      const childSegments = childTo.split('/').filter(Boolean)
      const parent = parentTo.endsWith('/') ? parentTo.slice(0, -1) : parentTo
      if (
        childSegments.length === 1 &&
        !childTo.startsWith(`${parent}/`) &&
        this.item.group !== 'image'
      ) {
        return `${parent}${childTo}`
      }

      return childTo
    },
    getGroupTargetPath() {
      const firstChild = this.children.find((child) => child && child.to)
      if (firstChild && typeof firstChild.to === 'string') {
        return firstChild.to
      }

      const current = this.item?.to
      if (!current || typeof current !== 'string') return null
      return current.startsWith('/') ? current : `/${current}`
    },
    async onGroupClick() {
      const targetPath = this.getGroupTargetPath()
      if (!targetPath || this.$route.path === targetPath) return
      const result = await this.$router.push(targetPath).catch(() => false)
      if (result === false && this.$route.path !== targetPath && typeof window !== 'undefined') {
        window.setTimeout(() => {
          if (this.$route.path !== targetPath) {
            window.location.assign(targetPath)
          }
        }, 380)
      }
    },
    genGroup(children) {
      return children
        .filter((item) => item.to)
        .map((item) => {
          const parent = item.group || this.item.group
          let group = `${parent}/${kebabCase(item.to)}`

          if (item.children) {
            group = `${group}|${this.genGroup(item.children)}`
          }

          return group
        })
        .join('|')
    },
  },
})
</script>
